import { useEffect, useRef } from "react";
import { api, API_URL, storePath } from "./api";

export type StoreEvent = { id: string; type: string; data: unknown };

// useStoreEvents keeps an SSE connection to the store's event stream. Each
// connection needs a single-use ticket (EventSource can't send a bearer
// token); on drop or server-requested reconnect it fetches a new ticket and
// resumes from the last event id, so nothing in the 5-minute window is missed.
export function useStoreEvents(storeId: string | undefined, onEvent: (e: StoreEvent) => void) {
  const handler = useRef(onEvent);
  handler.current = onEvent;

  useEffect(() => {
    if (!storeId) return;
    let closed = false;
    let es: EventSource | null = null;
    let lastId = "";
    let retry = 1000;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const connect = async () => {
      if (closed) return;
      try {
        const { ticket } = await api<{ ticket: string }>(storePath(storeId, "/events/ticket"), { method: "POST" });
        if (closed) return;
        const q = new URLSearchParams({ ticket });
        if (lastId) q.set("last_event_id", lastId);
        es = new EventSource(`${API_URL}${storePath(storeId, "/events/stream")}?${q}`);
        const dispatch = (ev: MessageEvent) => {
          lastId = ev.lastEventId || lastId;
          retry = 1000;
          let data: unknown = null;
          try {
            data = JSON.parse(ev.data);
          } catch {
            /* non-JSON payload */
          }
          handler.current({ id: ev.lastEventId, type: ev.type, data });
        };
        for (const type of [
          "notification.created", "provisioning.step", "provisioning.finished",
          "asset.ready", "asset.failed", "job.progress", "job.finished", "domain.updated", "theme.presence", "theme.file_changed", "theme.draft_changed",
        ]) es.addEventListener(type, dispatch as EventListener);
        const reopen = () => {
          es?.close();
          es = null;
          timer = setTimeout(connect, retry);
          retry = Math.min(retry * 2, 30000);
        };
        es.addEventListener("reconnect", () => { retry = 0; reopen(); });
        es.onerror = reopen; // tickets are single use: always reconnect with a new one
      } catch {
        timer = setTimeout(connect, retry);
        retry = Math.min(retry * 2, 30000);
      }
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      es?.close();
    };
  }, [storeId]);
}
